import { useState } from 'react'
import { motion } from 'motion/react'
import { formatPrecise } from '@/lib/format'
import { CountUp } from '@/components/dashboard/fx/CountUp'
import { EASE } from '@/components/dashboard/fx/motion'
import { OTHER_COLOR, SLOT_COLORS } from '@/components/dashboard/PartToWholeBreakdown'

interface SoundMixProps {
  data: { name: string; count: number }[]
  unitLabel: string
}

// A chunky stacked bar that sweeps in on scroll. Hover any legend entry or
// segment and everything else dims -- the whole thing reads as one control.
export function SoundMix({ data, unitLabel }: SoundMixProps) {
  const [active, setActive] = useState<string | null>(null)
  const total = data.reduce((sum, d) => sum + d.count, 0)
  if (total === 0) return null

  const segments = data.map((d, i) => ({
    ...d,
    percent: (d.count / total) * 100,
    color: d.name === 'Other' ? OTHER_COLOR : SLOT_COLORS[i % SLOT_COLORS.length],
  }))
  const dim = (name: string) => (active === null || active === name ? 1 : 0.2)

  return (
    <div className="flex flex-col gap-8" onPointerLeave={() => setActive(null)}>
      <div className="flex h-16 w-full gap-1 overflow-hidden rounded-2xl">
        {segments.map((s, i) => (
          <motion.div
            key={s.name}
            className="h-full origin-left cursor-pointer"
            style={{ width: `${s.percent}%`, backgroundColor: s.color }}
            initial={{ scaleX: 0, opacity: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            animate={{ opacity: dim(s.name) }}
            transition={{ scaleX: { duration: 1.2, delay: i * 0.08, ease: EASE }, opacity: { duration: 0.25 } }}
            onPointerEnter={() => setActive(s.name)}
            title={`${s.name}: ${formatPrecise(s.count)} ${unitLabel}`}
          />
        ))}
      </div>

      <ul className="grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2">
        {segments.map((s) => (
          <li
            key={s.name}
            onPointerEnter={() => setActive(s.name)}
            style={{ opacity: dim(s.name) }}
            className="flex cursor-default items-center gap-3 border-b border-border/60 py-3 transition-opacity duration-300"
          >
            <span className="size-3 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-medium capitalize">{s.name}</span>
              <span className="text-xs tabular-nums text-muted-foreground">
                {formatPrecise(s.count)} {unitLabel}
              </span>
            </div>
            <CountUp
              value={s.percent}
              suffix="%"
              className="font-display text-3xl font-semibold tracking-tighter"
            />
          </li>
        ))}
      </ul>
    </div>
  )
}
